import { Button, cn, formatDate as formatUiDate, useTranslation } from '@hbs-us/ui';
import { Link } from '@tanstack/react-router';
import { ChevronLeft } from 'lucide-react';
import { useId, useRef, useState } from 'react';

import { useMoneyFormatters } from '../_money/useMoneyFormatters.js';
import { BlueprintsTab } from './components/BlueprintsTab.js';
import { ChangeOrdersPanel } from './components/ChangeOrdersPanel.js';
import { CreateInvoiceForJob } from './components/CreateInvoiceForJob.js';
import { ExpenseIncomeDialogs } from './components/ExpenseIncomeDialogs.js';
import { JobMergeFlow } from './components/JobMergeFlow.js';
import { LaborTab } from './components/LaborTab.js';
import { ProfitabilityTab } from './components/ProfitabilityTab.js';
import type { JobDto, JobProfitabilityDto, JobStatus } from './contractsMirror.js';
import { useJobAccess } from './useJobAccess.js';

/**
 * The `/jobs/$id` job-detail view — the most data-rich Work screen, with the former standalone
 * job-financials screen FOLDED into a Profitability TAB (D-05).
 *
 * The detail is a proper `role="tablist"` / `role="tab"` / `role="tabpanel"` set, arrow-key
 * navigable (A11Y-01 — UI-SPEC detail sub-tab contract): Overview · Line Items · Expenses/Income ·
 * Profitability · Blueprints. **Profitability is a TAB, NOT a router route** — it renders the
 * server-computed core-D1 profit aggregate (never re-aggregated, never the payments vault).
 *
 * Every color is a locked `@hbs-us/ui` token (no hex); every numeral renders in IBM Plex Mono.
 */

type TabKey =
  | 'overview'
  | 'lineItems'
  | 'expensesIncome'
  | 'labor'
  | 'profitability'
  | 'changeOrders'
  | 'blueprints';
const TAB_ORDER: readonly TabKey[] = [
  'overview',
  'lineItems',
  'expensesIncome',
  'labor',
  'profitability',
  'changeOrders',
  'blueprints',
];

export function JobDetailView({
  job,
  profitability,
  candidateJobs,
}: {
  job: JobDto;
  profitability: JobProfitabilityDto;
  candidateJobs: JobDto[];
}) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const fmt = useMoneyFormatters();
  const { canEdit, canMerge } = useJobAccess();

  const [activeTab, setActiveTab] = useState<TabKey>('overview');
  const tablistId = useId();
  const tabRefs = useRef<Record<TabKey, HTMLButtonElement | null>>({
    overview: null,
    lineItems: null,
    expensesIncome: null,
    labor: null,
    profitability: null,
    changeOrders: null,
    blueprints: null,
  });

  // Roving-tabindex arrow-key navigation across the tablist (UI-SPEC A11Y contract).
  function onTabKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
    const currentIndex = TAB_ORDER.indexOf(activeTab);
    let nextIndex: number | null = null;
    if (event.key === 'ArrowRight') nextIndex = (currentIndex + 1) % TAB_ORDER.length;
    else if (event.key === 'ArrowLeft')
      nextIndex = (currentIndex - 1 + TAB_ORDER.length) % TAB_ORDER.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = TAB_ORDER.length - 1;
    if (nextIndex === null) return;
    event.preventDefault();
    const nextKey = TAB_ORDER[nextIndex];
    setActiveTab(nextKey);
    tabRefs.current[nextKey]?.focus();
  }

  function tabProps(key: TabKey) {
    const selected = activeTab === key;
    return {
      type: 'button' as const,
      role: 'tab' as const,
      id: `${tablistId}-tab-${key}`,
      'aria-selected': selected,
      'aria-controls': `${tablistId}-panel-${key}`,
      tabIndex: selected ? 0 : -1,
      ref: (node: HTMLButtonElement | null) => {
        tabRefs.current[key] = node;
      },
      onClick: () => setActiveTab(key),
      onKeyDown: onTabKeyDown,
      className: cn(
        'inline-flex h-11 shrink-0 items-center border-b-2 px-4 font-medium text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
        selected
          ? 'border-primary text-foreground'
          : 'border-transparent text-muted-foreground hover:text-foreground',
      ),
    };
  }

  function panelProps(key: TabKey) {
    return {
      role: 'tabpanel' as const,
      id: `${tablistId}-panel-${key}`,
      'aria-labelledby': `${tablistId}-tab-${key}`,
      hidden: activeTab !== key,
      tabIndex: 0,
    };
  }

  return (
    <div className="min-h-full bg-background px-6 py-8">
      <header className="mb-6">
        <Link
          to="/jobs"
          className="mb-3 inline-flex min-h-11 items-center gap-1 rounded-md pr-3 font-medium text-muted-foreground text-sm hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
          {t('jobs:actions.parentJobs')}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-center gap-3">
            <h1 className="font-display font-semibold text-3xl text-foreground">
              {job.jobName || t('jobs:table.unnamed', { id: job.id })}
            </h1>
            <span className="inline-flex items-center rounded-full border border-border bg-background px-2 py-0.5 font-medium text-muted-foreground text-xs">
              {job.status
                ? t(`jobs:status.${statusKey(job.status as JobStatus)}`, {
                    defaultValue: job.status,
                  })
                : t('jobs:table.none')}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {canEdit ? (
              <Button asChild variant="secondary">
                <Link to="/jobs/$id/edit" params={{ id: String(job.id) }}>
                  {t('jobs:actions.edit')}
                </Link>
              </Button>
            ) : null}
            <CreateInvoiceForJob job={job} />
          </div>
        </div>
      </header>

      <div
        role="tablist"
        aria-label={t('jobs:detail.tablistLabel')}
        className="mb-6 flex flex-nowrap gap-2 overflow-x-auto border-border border-b [mask-image:linear-gradient(to_right,black_calc(100%-2rem),transparent)] pe-8"
      >
        <button {...tabProps('overview')}>{t('jobs:detail.tabs.overview')}</button>
        <button {...tabProps('lineItems')}>{t('jobs:detail.tabs.lineItems')}</button>
        <button {...tabProps('expensesIncome')}>{t('jobs:detail.tabs.expensesIncome')}</button>
        <button {...tabProps('labor')}>{t('jobs:detail.tabs.labor')}</button>
        <button {...tabProps('profitability')}>{t('jobs:detail.tabs.profitability')}</button>
        <button {...tabProps('changeOrders')}>{t('jobs:detail.tabs.changeOrders')}</button>
        <button {...tabProps('blueprints')}>{t('jobs:detail.tabs.blueprints')}</button>
      </div>

      <section {...panelProps('overview')}>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="rounded-md border border-border bg-card p-6 lg:col-span-2">
            <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
              {t('jobs:detail.overviewHeading')}
            </h2>
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <Field label={t('jobs:detail.client')} value={job.clientName} t={t} />
              <Field label={t('jobs:detail.code')} value={job.jobCode} t={t} mono />
              <Field
                label={t('jobs:detail.contract')}
                value={fmt.formatCurrency(Number(job.contractAmount ?? 0))}
                t={t}
                mono
              />
              <Field
                label={t('jobs:detail.retainage')}
                value={fmt.percent(Number(job.retainagePercent ?? 0))}
                t={t}
                mono
              />
              <Field
                label={t('jobs:detail.startDate')}
                value={
                  job.startDate
                    ? formatUiDate(job.startDate, locale, { dateStyle: 'medium' })
                    : null
                }
                t={t}
                mono
              />
              <Field label={t('jobs:detail.soldBy')} value={job.soldBy ?? null} t={t} />
            </dl>
          </div>
          <div className="space-y-6 lg:col-span-1">
            <div className="rounded-md border border-border bg-card p-6">
              <h2 className="font-display font-semibold text-foreground text-lg">
                {t('jobs:detail.descriptionHeading')}
              </h2>
              <p className="mt-2 text-foreground text-sm">
                {job.jobDescription ? job.jobDescription : t('jobs:detail.noDescription')}
              </p>
            </div>

            <div className="rounded-md border border-border bg-card p-6">
              <h2 className="mb-3 font-display font-semibold text-foreground text-lg">
                {t('jobs:detail.linkedEstimatesHeading')}
              </h2>
              {job.sourceEstimateId ? (
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link
                      to="/estimates/$id"
                      params={{ id: String(job.sourceEstimateId) }}
                      className="font-medium text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                    >
                      {job.sourceEstimateNumber
                        ? t('jobs:detail.linkedEstimateLabel', {
                            number: job.sourceEstimateNumber,
                          })
                        : t('jobs:detail.linkedEstimateFallback', { id: job.sourceEstimateId })}
                    </Link>
                    {job.sourceEstimateCustomerName ? (
                      <span className="ml-2 text-muted-foreground">
                        {job.sourceEstimateCustomerName}
                      </span>
                    ) : null}
                  </li>
                </ul>
              ) : (
                <p className="text-muted-foreground text-sm">
                  {t('jobs:detail.noLinkedEstimates')}
                </p>
              )}
            </div>
          </div>
        </div>
      </section>

      <section {...panelProps('lineItems')}>
        <div className="rounded-md border border-border bg-card p-6">
          <h2 className="mb-4 font-display font-semibold text-foreground text-lg">
            {t('jobs:detail.lineItemsHeading')}
          </h2>
          <table className="w-full text-left text-sm">
            <caption className="sr-only">{t('jobs:detail.lineItemsCaption')}</caption>
            <tbody>
              <SummaryRow
                label={t('jobs:detail.contract')}
                value={fmt.formatCurrency(Number(job.contractAmount ?? 0))}
              />
              <SummaryRow
                label={t('jobs:detail.invoiced')}
                value={fmt.formatCurrency(Number(job.financials?.totalInvoiced ?? 0))}
              />
              <SummaryRow
                label={t('jobs:detail.collected')}
                value={fmt.formatCurrency(Number(job.financials?.totalCollected ?? 0))}
              />
              <SummaryRow
                label={t('jobs:detail.unpaidBalance')}
                value={fmt.formatCurrency(Number(job.financials?.unpaidInvoiceBalance ?? 0))}
              />
            </tbody>
          </table>
          <p className="mt-4 text-muted-foreground text-xs">{t('jobs:detail.lineItemsNote')}</p>
        </div>
      </section>

      <section {...panelProps('expensesIncome')}>
        <ExpenseIncomeDialogs job={job} />
      </section>

      <section {...panelProps('labor')}>
        <LaborTab jobId={job.id} />
      </section>

      <section {...panelProps('profitability')}>
        {/* D-05: the Profitability TAB renders the server core-D1 aggregate — never re-aggregated,
            never the payments vault. */}
        <ProfitabilityTab profit={profitability} />
      </section>

      <section {...panelProps('changeOrders')}>
        <ChangeOrdersPanel job={job} />
      </section>

      <section {...panelProps('blueprints')}>
        <div className="space-y-6">
          <BlueprintsTab jobId={job.id} />
          <ChangeOrdersPanel job={job} />
          {canMerge ? <JobMergeFlow primaryJob={job} candidateJobs={candidateJobs} /> : null}
        </div>
      </section>
    </div>
  );
}

function Field({
  label,
  value,
  t,
  mono = false,
}: {
  label: string;
  value: string | null;
  t: (key: string) => string;
  mono?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-muted-foreground text-xs uppercase tracking-wide">{label}</dt>
      <dd className={mono ? 'truncate font-mono text-foreground' : 'truncate text-foreground'}>
        {value ? value : t('jobs:detail.none')}
      </dd>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <tr className="border-border/60 border-b last:border-b-0">
      <th scope="row" className="py-2 pr-4 text-left font-medium text-muted-foreground">
        {label}
      </th>
      <td className="py-2 text-right font-mono font-semibold text-foreground">{value}</td>
    </tr>
  );
}

function statusKey(status: JobStatus): string {
  return status === 'On Hold' ? 'onHold' : status.toLowerCase();
}
